import { motion, MotionProps } from "framer-motion"
import React from "react"

type Props = {
  width?: number
  height?: number
  viewBox?: string
} & MotionProps

const repeatDelay = 3

export function Agent(props: Props) {
  const { width = 106, height = 88, viewBox = "0 0 53 44", ...rest } = props

  return (
    <motion.svg
      width={width}
      height={height}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      variants={{
        rest: {
          y: 0,
          scaleY: 1,
        },
        show: {
          scaleY: [1, 0.85, 1],
          y: [0, -10, 0],
          transition: {
            duration: 0.5,
            ease: "easeInOut",
            repeat: Infinity,
            repeatDelay,
          },
        },
      }}
      {...rest}
    >
      <motion.path
        d="M50.7628 22.92C50.7628 33.8657 39.7238 42.9944 25.7564 42.9944C11.789 42.9944 0.75 33.8657 0.75 22.92C0.75 11.9743 11.789 2.8457 25.7564 2.8457C39.7238 2.8457 50.7628 11.9743 50.7628 22.92Z"
        stroke="var(--mui-palette-text-primary)"
        strokeWidth="1.5"
        initial="rest"
        variants={{
          rest: { pathLength: 1, pathOffset: 0 },
          show: { pathLength: 1, pathOffset: 0 },
          spawning: {
            pathLength: 1,
            pathOffset: [-1, 0],
            transition: {
              duration: 1.5,
              ease: "linear",
              repeat: Infinity,
              repeatType: "loop",
              repeatDelay: 1.5,
            },
          },
        }}
        {...rest}
      />
      <motion.path
        d="M10.0327 18.431V22.5634C10.0327 23.668 10.9281 24.5634 12.0327 24.5634H48.2032C49.1282 24.5634 49.9326 23.929 50.1481 23.0294L51.2002 18.6384C48.361 17.4534 12.4619 18.2038 10.0327 18.431Z"
        fill="url(#paint0_linear_1016_1231)"
        variants={{
          rest: { opacity: 1 },
          show: {
            opacity: 1,
            y: [0, -5, -5, -5, -5, 0],
            transition: {
              duration: 0.5,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay,
            },
          },
          spawning: {
            opacity: [0, 1],
            transition: {
              duration: 1.5,
              ease: "linear",
              repeat: Infinity,
              repeatType: "loop",
              repeatDelay: 1.5,
            },
          },
        }}
        {...rest}
      />
      <motion.path
        d="M0.548096 21.9676C0.548096 21.9676 7.75361 18.6779 10.0327 18.431M51.2002 20.2678C50.046 20.1947 52.3023 18.7016 51.2002 18.6384M10.0327 18.431C12.4619 18.2038 48.361 17.4534 51.2002 18.6384M10.0327 18.431V22.5634C10.0327 23.668 10.9281 24.5634 12.0327 24.5634H48.2032C49.1282 24.5634 49.9326 23.929 50.1481 23.0294L51.2002 18.6384"
        stroke="var(--mui-palette-text-primary)"
        strokeWidth="1.5"
        variants={{
          rest: { pathLength: 1, pathOffset: 0 },
          show: {
            pathLength: 1,
            pathOffset: 0,
            y: [0, -5, -5, -5, -5, 0],
            transition: {
              duration: 0.5,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay,
            },
          },
          spawning: {
            pathLength: 1,
            pathOffset: [-1, 0],
            transition: {
              duration: 1.5,
              ease: "linear",
              repeat: Infinity,
              repeatType: "loop",
              repeatDelay: 1.5,
            },
          },
        }}
        {...rest}
      />
      <motion.path
        d="M44.3979 9.5886L42.9229 13.8223C42.8884 13.9191 42.8264 14.0027 42.7452 14.0616C42.6639 14.1206 42.5673 14.1522 42.4684 14.1522C42.3694 14.1522 42.2728 14.1206 42.1916 14.0616C42.1103 14.0027 42.0483 13.9191 42.0139 13.8223L40.5413 9.5886L36.4848 8.04919C36.392 8.01324 36.312 7.94853 36.2555 7.86373C36.199 7.77894 36.1687 7.67813 36.1687 7.57484C36.1687 7.47155 36.199 7.37074 36.2555 7.28595C36.312 7.20115 36.392 7.13643 36.4848 7.10049L40.5413 5.56361L42.0163 1.3299C42.0507 1.23307 42.1128 1.14956 42.194 1.09058C42.2752 1.03161 42.3718 1 42.4708 1C42.5698 1 42.6664 1.03161 42.7476 1.09058C42.8289 1.14956 42.8909 1.23307 42.9253 1.3299L44.4003 5.56361L48.4568 7.10302C48.5496 7.13896 48.6296 7.20368 48.6861 7.28848C48.7426 7.37327 48.7729 7.47408 48.7729 7.57737C48.7729 7.68066 48.7426 7.78147 48.6861 7.86626C48.6296 7.95106 48.5496 8.01577 48.4568 8.05172L44.3979 9.5886Z"
        fill="url(#paint1_linear_1016_1231)"
        stroke="var(--mui-palette-text-primary)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        variants={{
          rest: { scale: 0 },
          show: {
            scale: [1, 1],
            y: [0, -5, -5, -5, -5, 0],
            transition: {
              duration: 0.5,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay,
            },
          },
          spawning: { scale: 0 },
        }}
        {...rest}
      />
      <defs>
        <linearGradient
          id="paint0_linear_1016_1231"
          x1="13.1493"
          y1="20.0423"
          x2="50.0464"
          y2="25.3347"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F5EE38" />
          <stop offset="1" stopColor="#11EA0D" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_1016_1231"
          x1="14.7964"
          y1="10.3161"
          x2="42.4708"
          y2="14.1522"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="0.83" stopColor="#F5CD19" />
        </linearGradient>
      </defs>
    </motion.svg>
  )
}
